import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Link as RouterLink } from 'react-router-dom'
import Box from '@material-ui/core/Box'
import ButtonBase from '@material-ui/core/ButtonBase'
import Typography from '@material-ui/core/Typography'
import clsx from 'clsx'
import ChevronRightIcon from 'components/material-symbol/icons/ChevronRight'
import CheckIcon from 'components/material-symbol/icons/Check'
import ExpandMoreIcon from 'components/material-symbol/icons/ExpandMore'
import SectionTitle from 'components/section-title'
import { BenchmarkStatusChip } from 'pages/pga-coach/dashboard/performance-benchmarks/benchmark-chips'
import { BenchmarkInsightTip } from 'pages/pga-coach/dashboard/performance-benchmarks/benchmark-promo'
import { getMetricCalculationDescription } from 'pages/pga-coach/dashboard/performance-benchmarks/constants'
import { getMetricIcon } from 'pages/pga-coach/dashboard/performance-benchmarks/metric-icons'
import { getRecommendedStepIcon } from 'pages/pga-coach/dashboard/performance-benchmarks/recommended-step-icons'
import BenchmarkScale from './benchmark-scale'

const RecommendedStepRow = ({ step, isCompleted, classes }) => {
  const StepIcon = isCompleted ? CheckIcon : getRecommendedStepIcon(step.icon)

  return (
    <RouterLink
      to={step.actionPath}
      className={classes.recommendedRow}
      data-testid={`recommended-step-${step.id}`}
      data-completed={isCompleted ? 'true' : 'false'}
    >
      {StepIcon ? (
        <Box
          className={clsx(classes.recommendedIconCircle, {
            [classes.recommendedIconCircleCompleted]: isCompleted,
          })}
          aria-hidden
        >
          <StepIcon />
        </Box>
      ) : null}
      <Box className={classes.recommendedRowCopy}>
        <Typography
          component="p"
          className={clsx(classes.recommendedRowTitle, {
            [classes.recommendedRowTitleCompleted]: isCompleted,
          })}
        >
          {step.title}
        </Typography>
        {!isCompleted ? (
          <Typography
            component="p"
            className={classes.recommendedRowDescription}
          >
            {step.description}
          </Typography>
        ) : null}
      </Box>
      {isCompleted ? (
        <span
          className={classes.recommendedDoneBadge}
          data-testid="recommended-step-done-badge"
        >
          <CheckIcon className={classes.recommendedDoneBadgeIcon} aria-hidden />
          Done
        </span>
      ) : (
        <ChevronRightIcon
          className={classes.recommendedRowChevron}
          aria-hidden
        />
      )}
    </RouterLink>
  )
}

RecommendedStepRow.propTypes = {
  step: PropTypes.object.isRequired,
  isCompleted: PropTypes.bool.isRequired,
  classes: PropTypes.object.isRequired,
}

const BenchmarkDetailCard = ({
  metric,
  classes,
  defaultExpanded = false,
  recommendedStepCompletion = {},
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const MetricIcon = getMetricIcon(metric.id)
  const metricBodyId = `benchmark-metric-body-${metric.id}`

  return (
    <Box className={classes.metricItem} id={`benchmark-metric-${metric.id}`}>
      <ButtonBase
        component="div"
        className={clsx(classes.metricCardHeader, {
          [classes.metricCardHeaderExpanded]: expanded,
        })}
        onClick={() => setExpanded(current => !current)}
        aria-expanded={expanded}
        aria-controls={metricBodyId}
      >
        <Box className={classes.metricCardHeaderLeft}>
          {MetricIcon ? (
            <Box className={classes.metricIconCircle} aria-hidden>
              <MetricIcon />
            </Box>
          ) : null}
          <Box className={classes.metricCardHeaderText}>
            <Typography component="p" className={classes.metricTitle}>
              {metric.label}
            </Typography>
            <Typography
              component="p"
              className={classes.metricDescription}
              data-testid="metric-calculation-description"
            >
              {getMetricCalculationDescription(metric)}
            </Typography>
          </Box>
        </Box>
        <Box className={classes.metricCardHeaderRight}>
          <BenchmarkStatusChip metric={metric} />
          {expanded ? (
            <ExpandMoreIcon
              className={classes.metricCardHeaderChevron}
              aria-hidden
            />
          ) : (
            <ChevronRightIcon
              className={classes.metricCardHeaderChevron}
              aria-hidden
            />
          )}
        </Box>
      </ButtonBase>

      {expanded ? (
        <Box className={classes.metricBody} id={metricBodyId}>
          <Box className={classes.metricBodySection}>
            <BenchmarkScale metric={metric} classes={classes} />
          </Box>

          <Box
            className={classes.metricBodyDivider}
            data-testid="metric-body-divider"
            aria-hidden
          />

          <Box
            className={clsx(
              classes.metricBodySection,
              classes.metricBodySectionInsight,
            )}
          >
            <SectionTitle
              component="h3"
              className={classes.metricBodySectionTitle}
            >
              Insight
            </SectionTitle>
            <BenchmarkInsightTip
              body={metric.insightBody}
              tip={metric.insightTip}
            />
          </Box>

          <Box
            className={classes.metricBodyDivider}
            data-testid="metric-body-divider"
            aria-hidden
          />

          <Box
            className={clsx(
              classes.metricBodySection,
              classes.metricBodySectionRecommended,
            )}
          >
            <SectionTitle
              component="h3"
              className={classes.metricBodySectionTitle}
            >
              Recommended next steps
            </SectionTitle>
            <Box className={classes.recommendedList}>
              {metric.recommendedSteps.map(step => (
                <RecommendedStepRow
                  key={step.id}
                  step={step}
                  isCompleted={Boolean(recommendedStepCompletion[step.id])}
                  classes={classes}
                />
              ))}
            </Box>
          </Box>
        </Box>
      ) : null}
    </Box>
  )
}

BenchmarkDetailCard.propTypes = {
  metric: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  defaultExpanded: PropTypes.bool,
  recommendedStepCompletion: PropTypes.object,
}

export default BenchmarkDetailCard
